import { PlusOutlined, PoweroffOutlined } from '@ant-design/icons';
import {
  Button,
  Drawer,
  Form,
  Input,
  Select,
  Space,
  Tooltip,
  Badge,
  message,
} from 'antd';
import axios from 'axios';
import { AccessControl } from 'components/AccessControl';
import ClientsSelect from 'components/ClientsSelect';
import { RecordFields } from 'components/RecordFields';
import { CONTENT_SPAN, LABEL_SPAN } from 'constants/table';
import { useDocumentTitle } from 'hooks/useDocumentTitle';
import { useDrawers } from 'hooks/useDrawers';
import { useFilters } from 'hooks/useFilters';
import { useHeaderChanger } from 'hooks/useHeaderChanger';
import { usePagination } from 'hooks/usePagination';
import { useQueryParam } from 'hooks/useQueryParam';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { ServicesService } from 'services/ServicesService';
import { fetchPackages } from 'store/reducers/staticHandbooksReducer';
import { ActionsWrapper } from 'ui/ActionsWrapper';
import { handleDownloadExel } from 'utils/files';
import { requiredRule } from 'utils/forms/validationRules';
import {
  clientBoughtStatus,
  serviceOffReasons,
} from 'utils/handbooks/clientBoughtHandbooks';
import { ApplyServiceForm } from './ApplyServiceForm';
import { HistoryTable } from './HistoryTable';

const PAGE_TITLE = 'Продажи';

function History() {
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  useDocumentTitle(PAGE_TITLE);
  const { setCount } = useHeaderChanger(PAGE_TITLE);
  const { packagesData } = useSelector((state) => state.staticHandbooks);
  const [history, setHistory] = useState([]);
  const [activeRowRecord, setActiveRowRecord] = useState(null);
  const [selectedRows, setSelectedRows] = useState([]);
  const { filters, handleFilters, resetFilters } = useFilters({
    client_id: '',
    status: null,
    type: null,
    f_price: undefined,
    t_price: undefined,
    f_bought: null,
    t_bought: null,
    package_ids: [],
    start_date: null,
    end_date: null,
    days_left: null,
    creator_role_names: [],
    creator_full_name: null,
  });
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState({
    total_amount: 0,
    total_amount_origin: 0,
  });
  const [offServiceForm, setOffServiceForm] = useState(false);
  const {
    page,
    perPage,
    totalRows,
    setPagination,
    setTotalRows,
    resetPagination,
  } = usePagination();
  const {
    state: { formIsActive },
    openCreateForm,
    closeForm,
  } = useDrawers();
  const clientId = useQueryParam('client_id');
  const { client_id: selectedClient } = filters;

  useEffect(() => {
    if (packagesData.items.length === 0) {
      dispatch(fetchPackages());
    }
  }, []);

  const recordFields = useMemo(() => {
    return [
      {
        label: 'Тип',
        key: 'type',
        computedValue: activeRowRecord?.type === 'package' ? 'Тариф' : 'Услуга',
      },
      {
        label: 'Название',
        key: 'package_title',
      },
      {
        label: 'Цена',
        key: 'price',
      },
      {
        label: 'Скидка',
        key: 'discount_percent',
      },
      {
        label: 'Опция',
        key: 'option_title',
      },
      {
        label: 'Дата покупки',
        key: 'created_date',
      },
      {
        label: 'Дата начала',
        key: 'start_date',
      },
      {
        label: 'Дата окончания',
        key: 'end_date',
      },
      {
        label: 'Оставшиеся дни',
        key: 'days_left',
      },
      {
        label: 'Статус',
        key: 'status',
        computedValue: activeRowRecord
          ? clientBoughtStatus[activeRowRecord.status]
          : null,
      },
      {
        label: 'Причина отключения',
        key: 'reason',
      },
      {
        label: 'Комментарий',
        key: 'comment',
      },
      {
        label: 'Дата отключения',
        key: 'offDate',
      },
    ];
  }, [activeRowRecord]);

  const renderDrawerActions = () => {
    if (!activeRowRecord) {
      return null;
    }

    if (activeRowRecord.status === 'active' && activeRowRecord.payment_id) {
      return (
        <AccessControl pageId="clientBought" action="update">
          <Tooltip placement="bottomLeft" title="Отключить услугу/тариф">
            <Button
              type="default"
              icon={<PoweroffOutlined />}
              onClick={() => setOffServiceForm(true)}
            />
          </Tooltip>
        </AccessControl>
      );
    }

    return null;
  };

  const fetchHistory = useCallback(async () => {
    if (!page || !perPage) {
      return;
    }

    setLoading(true);

    try {
      const { items, total, total_amount, total_amount_origin } =
        await ServicesService.getServicesHistory(page, perPage, filters);

      setHistory(items);
      setTotalRows(total);
      setCount(total);
      setSummary({ total_amount, total_amount_origin });
    } catch (err) {
      message.error('Не удалось загрузить историю услуг');
    } finally {
      setLoading(false);
    }
  }, [page, perPage, filters]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  const handleOffService = async (values) => {
    const payload = {
      ...values,
      payment_service_id: activeRowRecord.payment_id,
    };

    const hide = message.loading('Отключение тарифа...');

    try {
      const res = await axios.post('payments/v1/offService/', payload);

      if (res.status === 200) {
        form.resetFields();
        setOffServiceForm(false);
        setActiveRowRecord(null);
        fetchHistory();
        message.success('Пакет/услуга успешно отключена');
      }
    } catch (err) {
      message.error('Ошибка при отключении тарифа');
    } finally {
      hide();
    }
  };

  const handleDownloadExcelFile = async () => {
    const hide = message.loading('Экспортирование в Excel');

    try {
      const res = await axios.post(
        'org/v1/clientBoughtService/export/',
        selectedRows
      );

      if (res) {
        handleDownloadExel(res.data, 'Продажи.xlsx');
      }
    } catch (error) {
      message.error('Ошибка при экспорте');
    } finally {
      hide();
    }
  };

  const handleFetchDetails = async (record) => {
    if (!record?.payment_id) {
      return;
    }

    try {
      const res = await axios.get(`org/v1/clientBoughtService/${record.id}/`);

      if (res.status === 200) {
        setActiveRowRecord({ ...res.data, client_id: record?.client_id });
      }
    } catch (err) {
      message.error('Произошла ошибка');
    }
  };

  const handleClientSelect = (id = '') => {
    resetFilters();
    handleFilters({ client_id: id });
    resetPagination();
  };

  const extraButton = (
    <Button
      onClick={() =>
        navigate(`/clients/all-clients?client_id=${activeRowRecord?.client_id}`)
      }
    >
      Перейти к клиенту
    </Button>
  );

  return (
    <>
      <ActionsWrapper>
        <ClientsSelect
          pageTitle={PAGE_TITLE}
          clientId={clientId}
          onSelect={handleClientSelect}
          onClear={handleClientSelect}
        />

        <AccessControl pageId="clientBought" action="create">
          <Space>
            <Badge
              count={selectedRows.length}
              size="small"
              overflowCount={100000}
              offset={[-10, 7]}
            >
              <Button
                disabled={selectedRows.length <= 0}
                type="default"
                icon={<PlusOutlined />}
                onClick={() => {
                  handleDownloadExcelFile();
                }}
              >
                Экспортировать в Excel
              </Button>
            </Badge>

            <Button
              disabled={!selectedClient}
              type="primary"
              icon={<PlusOutlined />}
              onClick={openCreateForm}
            >
              Создать
            </Button>
          </Space>
        </AccessControl>
      </ActionsWrapper>

      <HistoryTable
        items={history}
        loading={loading}
        page={page}
        pageSize={perPage}
        totalRows={totalRows}
        packageServices={packagesData.items}
        filters={filters}
        summary={summary}
        handleChange={(i) => {
          setSelectedRows(i);
        }}
        selectedRows={selectedRows}
        onChange={setPagination}
        onRowClick={handleFetchDetails}
        onFilter={(val) => {
          handleFilters(val);
          resetPagination();
        }}
      />

      <Drawer
        title="Детальная информация"
        width="50%"
        onClose={() => setActiveRowRecord(null)}
        visible={!!activeRowRecord}
        extra={
          <Space>
            {extraButton}
            {renderDrawerActions()}
          </Space>
        }
      >
        {activeRowRecord && (
          <>
            <RecordFields record={activeRowRecord} fields={recordFields} />

            <Drawer
              title="Отключение тарифа/услуги"
              width="40%"
              onClose={() => setOffServiceForm(false)}
              visible={offServiceForm}
            >
              <Form
                form={form}
                onFinish={handleOffService}
                layout="horizontal"
                labelCol={{
                  span: LABEL_SPAN,
                }}
                wrapperCol={{ span: CONTENT_SPAN }}
                labelAlign="right"
              >
                <Form.Item
                  name="reason_off"
                  label="Причина отключения"
                  rules={[requiredRule]}
                >
                  <Select allowClear showArrow placeholder="Выберите причину">
                    {serviceOffReasons.map((reason) => (
                      <Select.Option key={reason} value={reason}>
                        {reason}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>

                <Form.Item name="comment" label="Комментарий">
                  <Input.TextArea
                    showCount
                    maxLength={180}
                    allowClear
                    placeholder="Введите коментарий"
                    rows={4}
                  />
                </Form.Item>

                <Form.Item wrapperCol={{ offset: CONTENT_SPAN }}>
                  <Button type="primary" htmlType="submit">
                    Отключить
                  </Button>
                </Form.Item>
              </Form>
            </Drawer>
          </>
        )}
      </Drawer>

      <ApplyServiceForm
        servicesList={packagesData.items}
        servicesLoading={packagesData.loading}
        selectedClientId={selectedClient}
        isActive={formIsActive}
        onClose={closeForm}
        onSuccess={() => {
          closeForm();
          fetchHistory();
        }}
      />
    </>
  );
}

export { History };
